import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {Page} from '@/types';

export type ProductBenefitBannersResponse = {
  product_benefit_banners: Array<{
    id: string;
    platform: {
      id: string;
      name: string;
      value: string;
    };
    pageType: {
      id: string;
      name: string;
      value: string;
    };
    type: string;
    headline: string;
    backgroundColor: string;
    benefits: Array<{
      product_benefits_id: {
        tooltipText: string;
        keyfact: string;
        headline: string;
        icon: {
          id: string;
          filename_disk: string;
        };
      };
    }>;
  }>;
};

export const productBenefitBanners = () => `
  product_benefit_banners(
    filter: {
      status: {
        _eq: "published"
      },
      platform: {
        agent: {
          _eq: "${import.meta.env.VITE_PORTAL}"
        }
      },
      pageType: {
        value: {
          _eq: "sea"
        }
      },
    },
    limit: 1
  ) {
    id
    platform {
      id
      name
      value
    }
    pageType {
      id
      name
      value
    }
    type
    headline
    backgroundColor
    benefits {
      product_benefits_id {
        tooltipText
        keyfact
        headline
        icon {
          id
          filename_disk
        }
      }
    }
  }
`;

export type BadgesResponse = {
  badges: Array<{
    id: string;
    value: string;
    content: string;
    rotateRight: boolean;
    pageTypes: Array<{
      page_types_id: {
        id: string;
        value: string;
        name: string;
      };
    }>;
    badgeColor: string;
    textColor: string;
    badgeType: string;
    publishDate: string;
    expiryDate: string;
  }>;
};

export const badges = () => `
  badges(
    filter: {
      status: {
        _eq: "published"
      },
      publishDate: {
        _lte: "$NOW"
      },
      platforms: {
        platforms_id: {
          agent: {
            _eq: "${import.meta.env.VITE_PORTAL}"
          }
        }
      }
    },
    limit: 6
  ) {
    id
    value
    content
    rotateRight
    pageTypes {
      page_types_id {
        id
        value
        name
      }
    }
    badgeColor
    textColor
    badgeType
    publishDate
    expiryDate
  }
`;

export type ServiceBenefitsResponseBenefit = {
  id: string;
  icon: {
    filename_disk: string;
  };
  title: string;
  description?: string;
};

export type ServiceBenefitsResponse = {
  data: {
    service_center_benefits: Array<ServiceBenefitsResponseBenefit>;
  };
};

export const benefits = () => `
  service_center_benefits(
    filter: {
      status: {
        _eq: "published"
      },
      platforms: {
        platforms_id: {
          agent: {
            _eq: "${import.meta.env.VITE_PORTAL}"
          }
        }
      }
    }
  ) {
    id
    icon {
      filename_disk
    }
    title
    description
  }
`;

export type AgentText = {
  service_center_agent_texts_id: {
    destination_id: string;
    text: string;
    page_type?: Page;
    travel_types?: Array<{
      travelTypes_id: {
        value: TravelType;
      };
    }>;
  };
};

export type ServiceCenterAgentResponseAgent = {
  id: string;
  portrait: {
    filename_disk: string;
  };
  phone_numbers: Array<{
    service_center_agent_phone_numbers_id: {
      number: string;
      page_type: Page;
      travel_types: Array<{
        travelTypes_id: {
          value: TravelType;
        };
      }>;
      devices: Array<{
        device_id: {
          value: 'sm' | 'md' | 'lg';
        };
      }>;
    };
  }>;
  title: string;
  subtitle?: string;
  old_title?: string;
  old_title_offers?: string;
  old_subtitle?: string;
  texts: Array<AgentText>;
};

export type AgentResponse = {
  data: {
    service_center_agents: Array<ServiceCenterAgentResponseAgent>;
  };
};

export const agents = ({
  id,
  page,
  destinationId,
  travelType,
}: {
  id?: string;
  page: Page;
  destinationId: string;
  travelType: TravelType;
}) => `
  service_center_agents(
    filter: {
      ${
        id
          ? `      id: {
        _eq: "${id}"
      }`
          : ''
      }
      status: {
        _eq: "published"
      },
      platforms: {
        platforms_id: {
          agent: {
            _eq: "${import.meta.env.VITE_PORTAL}"
          }
        }
      }
    }
  ) {
    id
    portrait {
      filename_disk
    }
    phone_numbers {
      service_center_agent_phone_numbers_id {
        number
        page_type
        travel_types {
          travelTypes_id {
            value
          }
        }
        devices {
          device_id {
            value
          }
        }
      }
    }
    title
    subtitle
    old_title
    old_title_offers
    old_subtitle
    texts(
      filter: {
        _or: [
          {
            service_center_agent_texts_id: {
              page_type: {
                _eq: "${page}"
              }
              travel_types: {
                travelTypes_id: {
                  value: {
                    _eq: "${travelType}"
                  }
                }
              }
            }
          },
          {
            _or: [
              {
                service_center_agent_texts_id: {
                  destination_id: {
                    _eq: "default"
                  }
                }
              },
              {
                service_center_agent_texts_id: {
                  destination_id: {
                    _eq: "${destinationId}"
                  }
                }
              }
            ]
          }
        ]
      }
    ) {
      service_center_agent_texts_id {
        destination_id
        page_type
        travel_types {
          travelTypes_id {
            value
          }
        }
        text
      }
    }
  }
`;
